import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { WorkspaceLoadingBrandMoment } from '@triedge/ai-assistant-ui/components';
import { useLocation } from 'react-router-dom';
import { store, type RootState } from '../state/store';
import {
  createYstnjWorkspacePreloadDebugBridge,
  createYstnjWorkspacePreloadRunner,
  isYstnjWorkspacePreloadEnabled,
  isYstnjWorkspacePreloadDebugEnabled,
  recordYstnjWorkspacePreloadEvent,
  resolveYstnjWorkspacePreloadModuleForPath,
  resolveYstnjWorkspacePreloadRouteParams,
  selectYstnjCriticalPreloadOperationsForRoute,
  type YstnjWorkspacePreloadContext,
} from './ystnjWorkspacePreload';

const MIN_WORKSPACE_TRANSITION_MS = 180;

const waitForMinimumTransition = async (startedAt: number): Promise<void> => {
  const remaining = MIN_WORKSPACE_TRANSITION_MS - (Date.now() - startedAt);
  if (remaining <= 0) {
    return;
  }
  await new Promise((resolve) => {
    window.setTimeout(resolve, remaining);
  });
};

export const WorkspaceTransitionScreen = ({
  description = 'Preparing the authenticated workspace and route data.',
}: {
  description?: string;
}) => {
  useEffect(() => {
    const windowWithCounter = window as Window & {
      __YSTNJ_WORKSPACE_LOADER_APPEARANCES__?: number;
      __YSTNJ_WORKSPACE_LOADER_SESSION_RECORDED__?: boolean;
    };
    if (windowWithCounter.__YSTNJ_WORKSPACE_LOADER_SESSION_RECORDED__) {
      return;
    }
    windowWithCounter.__YSTNJ_WORKSPACE_LOADER_SESSION_RECORDED__ = true;
    windowWithCounter.__YSTNJ_WORKSPACE_LOADER_APPEARANCES__ =
      (windowWithCounter.__YSTNJ_WORKSPACE_LOADER_APPEARANCES__ ?? 0) + 1;
  }, []);

  return (
    <section
      data-testid="ystnj-workspace-transition"
      aria-live="polite"
      className="grid min-h-screen place-items-center bg-[#090D11] px-6 py-16 text-[#E8EFEF]"
    >
      <div className="flex w-full max-w-[420px] flex-col items-center">
        <WorkspaceLoadingBrandMoment
          active
          surfaceTheme="dark"
          artifactTestId="ystnj-workspace-loading-three-artifact"
          className="mb-7"
          artifactClassName="h-36 w-36 bg-[#05090A]/70 shadow-[0_24px_80px_rgba(40,226,167,0.16)] sm:h-40 sm:w-40"
          labelTestId="ystnj-workspace-loading-brand-wordmark"
        />
        <div className="w-full rounded-xl border border-white/10 bg-[#020506]/[0.92] px-8 py-8 shadow-[0_30px_100px_rgba(15,23,42,0.42)]">
          <h1 className="text-2xl font-extrabold text-[#ECEFFC]">
            Preparing your workspace
          </h1>
          <p className="mt-3 text-[15px] leading-7 text-[#A7AFBE]">
            {description}
          </p>
        </div>
      </div>
    </section>
  );
};

export const WorkspacePreloadProvider = ({
  children,
  runtimePersona,
  actorId,
}: {
  children: ReactNode;
  runtimePersona: string | null;
  actorId?: string | null;
}) => {
  const location = useLocation();
  const isEnabled = isYstnjWorkspacePreloadEnabled();
  const [isUnlocked, setIsUnlocked] = useState(() => !isEnabled);
  const hasUnlockedRef = useRef(false);
  const backgroundStartedRef = useRef(false);
  const debugContextRef = useRef<YstnjWorkspacePreloadContext>({
    actorId,
    persona: runtimePersona,
    routePath: location.pathname,
    routeParams: resolveYstnjWorkspacePreloadRouteParams(location.pathname, location.search),
  });
  const runner = useMemo(
    () => createYstnjWorkspacePreloadRunner({
      dispatch: store.dispatch,
      getState: () => store.getState() as RootState,
      onTelemetry: recordYstnjWorkspacePreloadEvent,
    }),
    [],
  );

  useEffect(() => {
    debugContextRef.current = {
      actorId,
      persona: runtimePersona,
      routePath: location.pathname,
      routeParams: resolveYstnjWorkspacePreloadRouteParams(location.pathname, location.search),
    };
  }, [actorId, location.pathname, location.search, runtimePersona]);

  useEffect(() => {
    if (!isEnabled || !isYstnjWorkspacePreloadDebugEnabled()) {
      return undefined;
    }
    window.__YSTNJ_WORKSPACE_PRELOAD_DEBUG__ = createYstnjWorkspacePreloadDebugBridge({
      getState: () => store.getState() as RootState,
      getContext: () => debugContextRef.current,
    });
    return () => {
      delete window.__YSTNJ_WORKSPACE_PRELOAD_DEBUG__;
    };
  }, [isEnabled]);

  useEffect(() => {
    if (!isEnabled) {
      hasUnlockedRef.current = true;
      setIsUnlocked(true);
      return undefined;
    }
    if (hasUnlockedRef.current) {
      return undefined;
    }

    let cancelled = false;
    const moduleId = resolveYstnjWorkspacePreloadModuleForPath(location.pathname);
    const context: YstnjWorkspacePreloadContext = {
      actorId,
      persona: runtimePersona,
      routePath: location.pathname,
      routeParams: resolveYstnjWorkspacePreloadRouteParams(location.pathname, location.search),
    };

    const unlock = async () => {
      const startedAt = Date.now();
      const criticalOperationIds = moduleId
        ? selectYstnjCriticalPreloadOperationsForRoute(location.pathname, 'eager')
        : [];
      try {
        await runner.run({
          context,
          phases: ['eager'],
          moduleIds: moduleId ? [moduleId] : [],
          operationIds: criticalOperationIds,
        });
      } finally {
        await waitForMinimumTransition(startedAt);
        if (cancelled) {
          return;
        }
        hasUnlockedRef.current = true;
        setIsUnlocked(true);

        if (!backgroundStartedRef.current) {
          backgroundStartedRef.current = true;
          void runner.run({
            context,
            phases: ['eager'],
          });
        }
      }
    };

    void unlock();
    return () => {
      cancelled = true;
    };
  }, [actorId, isEnabled, location.pathname, location.search, runner, runtimePersona]);

  useEffect(() => {
    if (!isEnabled || !hasUnlockedRef.current) {
      return undefined;
    }
    const moduleId = resolveYstnjWorkspacePreloadModuleForPath(location.pathname);
    if (!moduleId) {
      return undefined;
    }
    const signal = { aborted: false };
    const context: YstnjWorkspacePreloadContext = {
      actorId,
      persona: runtimePersona,
      routePath: location.pathname,
      routeParams: resolveYstnjWorkspacePreloadRouteParams(location.pathname, location.search),
    };
    void runner.run({
      context,
      phases: ['inline', 'pagination'],
      moduleIds: [moduleId],
      signal,
    });
    return () => {
      signal.aborted = true;
    };
  }, [actorId, isEnabled, isUnlocked, location.pathname, location.search, runner, runtimePersona]);

  if (!isUnlocked) {
    return <WorkspaceTransitionScreen />;
  }

  return <>{children}</>;
};

export const WorkspaceTransitionGate = WorkspacePreloadProvider;
